import { useEffect, useState } from "react";
import Link from "next/link";
import { ROUTE } from "@/route/app_routes.js";
import { getCategories, getPlatformStats } from "@/services/courseService";

// "All courses" counts as one of the visible slots, so this leaves 5 real
// categories showing before the reveal toggle -- enough to feel like a choice,
// not a wall of pills.
const VISIBLE_CHIP_COUNT = 6;

const CategoryBanner = ({ category }) => {

  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [chipsExpanded, setChipsExpanded] = useState(false);

  useEffect(() => {
    getCategories().then((res) => setCategories(res.data || [])).catch(() => {});
    getPlatformStats().then((res) => setStats(res.data)).catch(() => {});
  }, []);

  return (

    <>
      <div className="rbt-banner-content-top rbt-course-hero-saucy">
        <div className="rbt-course-hero-saucy__glow rbt-course-hero-saucy__glow--one" aria-hidden="true"></div>
        <div className="rbt-course-hero-saucy__glow rbt-course-hero-saucy__glow--two" aria-hidden="true"></div>
        <div className="rbt-course-hero-saucy__grid-pattern" aria-hidden="true"></div>

        <div className="container">
          <div className="rbt-course-hero-saucy__layout">

            <div className="rbt-course-hero-saucy__main">
              <ul className="page-list">
                <li className="rbt-breadcrumb-item"> <Link href="/">Home</Link> </li>
                <li> <div className="icon-right"> <i className="feather-chevron-right"></i> </div> </li>
                {category ? (
                  <>
                    <li className="rbt-breadcrumb-item"> <Link href={ROUTE.course}>All Courses</Link> </li>
                    <li> <div className="icon-right"> <i className="feather-chevron-right"></i> </div> </li>
                    <li className="rbt-breadcrumb-item active">{category}</li>
                  </>
                ) : (
                  <li className="rbt-breadcrumb-item active"> {"All Courses"} </li>
                )}
              </ul>

              <span className="rbt-course-hero-saucy__eyebrow">
                <i className="feather-zap"></i> 100% free, forever
              </span>

              <div className="title-wrapper">
                <h1 className="title mb--0">{category || "Every course. Completely free."}</h1>
              </div>
              <p className="description">
                {category
                  ? `Free, project-based ${category} courses — self-paced, with quizzes and a certificate when you finish.`
                  : "No tuition, no trials, no upsells. Pick a course, start today, and walk away with a certificate."}
              </p>

              {categories.length > 0 && (() => {
                // Keep the active category in the always-visible window instead of letting
                // it get stranded behind "Show more" when its alphabetical position lands
                // past the fold.
                const ordered = category
                  ? [...categories].sort((a, b) => (a.name === category ? -1 : b.name === category ? 1 : 0))
                  : categories;
                const visibleSlots = Math.max(1, VISIBLE_CHIP_COUNT - 1); // "All courses" takes 1 slot
                const visible = chipsExpanded ? ordered : ordered.slice(0, visibleSlots);
                const hiddenCount = ordered.length - visible.length;

                return (
                  <div className="rbt-course-hero-chips">
                    <Link href={ROUTE.course} className={`rbt-course-hero-chip ${!category ? "active" : ""}`}>
                      All courses
                    </Link>
                    {visible.map((cat) => (
                      <Link
                        key={cat.slug}
                        href={`${ROUTE.course}?category=${encodeURIComponent(cat.name)}`}
                        className={`rbt-course-hero-chip ${category === cat.name ? "active" : ""}`}
                      >
                        {cat.name}
                      </Link>
                    ))}
                    {hiddenCount > 0 && (
                      <button
                        type="button"
                        className="rbt-course-hero-chip rbt-course-hero-chip--toggle"
                        onClick={() => setChipsExpanded(true)}
                        aria-expanded={chipsExpanded}
                      >
                        +{hiddenCount} more <i className="feather-chevron-down"></i>
                      </button>
                    )}
                    {chipsExpanded && ordered.length > visibleSlots && (
                      <button
                        type="button"
                        className="rbt-course-hero-chip rbt-course-hero-chip--toggle"
                        onClick={() => setChipsExpanded(false)}
                        aria-expanded={chipsExpanded}
                      >
                        Show less <i className="feather-chevron-up"></i>
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>

            {stats && (
              <div className="rbt-course-hero-saucy__visual">
                <div className="rbt-course-hero-stat rbt-course-hero-stat--lg">
                  <span className="rbt-course-hero-stat__icon"><i className="feather-book-open"></i></span>
                  <div>
                    <strong>{stats.total_courses}+</strong>
                    <span>Free courses</span>
                  </div>
                </div>
                <div className="rbt-course-hero-stat">
                  <span className="rbt-course-hero-stat__icon"><i className="feather-layers"></i></span>
                  <div>
                    <strong>{stats.total_categories}</strong>
                    <span>Skill tracks</span>
                  </div>
                </div>
                <div className="rbt-course-hero-stat">
                  <span className="rbt-course-hero-stat__icon"><i className="feather-users"></i></span>
                  <div>
                    <strong>{stats.total_students}+</strong>
                    <span>Learners</span>
                  </div>
                </div>
                <div className="rbt-course-hero-badge">
                  <i className="feather-award"></i>
                  <span>Certificate on completion</span>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </>

  );

};

export default CategoryBanner;
